<template>
  <div
    class="subscription-banner__wrapper"
    :class="`subscription-banner--${bannerState}`"
  >
    <div class="subscription-banner">
      <div class="subscription-banner__content">
        <gl-icon
          v-if="bannerState === 'expiring'"
          :height="24"
          name="I-Info-Fill"
          white
          :width="24"
        />
        <gl-icon
          v-else
          content-primary
          :height="24"
          name="I-Lock-Fill"
          :width="24"
        />
        <span class="subscription-banner__text">
          <template v-if="bannerState === 'expiring'">
            Your subscription expires in {{ getSubscriptionDaysUntilEnd }}
            {{ getSubscriptionDaysUntilEnd === 1 ? 'day' : 'days' }}. Renew now
            to ensure uninterrupted service.
          </template>
          <template v-else>
            Your subscription has ended. Your data is safe, but core features
            are locked.
          </template>
        </span>
      </div>
      <GlButton
        class="subscription-banner__button"
        :loading="loading"
        size="medium"
        title="Renew Subscription"
        white
        @click="onRenewClick"
      />
    </div>
  </div>
</template>

<script>
// vuex
import { mapActions, mapGetters } from 'vuex'
// components
import GlIcon from '@/components/gl-icon'
import GlButton from '@/components/gl-button'
// utils
import { openProfilePlan } from '@/utils/go-to-route'
// services
import { openBillingPortal } from '@/services/billing/subscription'

export default {
  name: 'SubscriptionBanner',
  components: {
    GlIcon,
    GlButton,
  },
  data() {
    return { loading: false }
  },
  computed: {
    ...mapGetters('subscribe', [
      'getSubscriptionBannerState',
      'getSubscriptionDaysUntilEnd',
    ]),
    ...mapGetters('payment', ['getCurrentPlan']),
    bannerState() {
      return this.getSubscriptionBannerState
    },
  },
  methods: {
    ...mapActions('payment', ['reactivateSubscription', 'fetchCurrentPlan']),
    openProfilePlan,
    async onRenewClick() {
      if (this.getSubscriptionBannerState === 'ended') {
        if (this.getCurrentPlan.status === 'past_due') {
          this.goToBillingPortal()
        } else if (this.getCurrentPlan.hadSubscription) {
          this.openProfilePlan({
            planTier: this.getCurrentPlan.previousPlanTier,
            billingCycle: this.getCurrentPlan.previousBillingCycle,
            isTrialRequest: false,
            actionStatus: 'renew',
          })
        } else {
          this.$router.push({ name: 'changePlan' })
        }
        return
      } else if (this.getSubscriptionBannerState === 'expiring') {
        try {
          this.loading = true
          const response = await this.reactivateSubscription()
          if (response && response.success) {
            this.$toast?.add?.({
              severity: 'success',
              summary: 'Success',
              detail: 'Subscription reactivated successfully.',
              life: 5000,
            })
          } else {
            this.$toast?.add?.({
              severity: 'error',
              summary: 'Error',
              detail: response.message || 'Failed to reactivate subscription.',
              life: 5000,
            })
          }
        } catch (error) {
          this.$toast?.add?.({
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to reactivate subscription.',
            life: 5000,
          })
        } finally {
          this.loading = false
        }
      }

      this.fetchCurrentPlan()
    },
    goToBillingPortal() {
      openBillingPortal(this.$store, {
        returnUrl: window.location.href,
        onError: message => {
          this.$toast?.add?.({
            severity: 'error',
            summary: 'Error',
            detail: message,
            life: 5000,
          })
        },
      })
    },
  },
}
</script>

<style scoped>
.subscription-banner__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.subscription-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: var(--main-content-width);
  max-width: var(--main-content-max-width);
  gap: 16px;
  padding: 12px 0;
  flex-shrink: 0;
}

.subscription-banner--expiring {
  background-color: var(--Brand-Main);
  color: var(--Basic-A-900, #fff);
}

.subscription-banner--ended {
  background-color: var(--Warning-Main, #ffa305);
  color: var(--Basic-900, #091540);
}

.subscription-banner__content {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.subscription-banner__text {
  font-size: var(--Size-M, 14px);
  font-weight: 600;
  line-height: var(--Line-Height-L, 20px);
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
}

@media (max-width: 767px) {
  .subscription-banner {
    flex-direction: column;
    align-items: flex-start;
  }
  .subscription-banner__button {
    margin-left: 28px;
  }
}
</style>
